import { useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import ApplicationCooLayout from '../../layout/application-coo-layout';
import { P2TextDescription, P2Button, P2Checkbox } from "react-bootstrap-common-script";
import { AdditionalAlert } from '../../components/alert';
import { BankIcon, ClockSvg, CurrencySignSVG, DocumentIcon, DollarIcon, EligibilityIcon, EndorsementIcon, FileCheckSvg, InfoSvg } from '../../assets/svg';
import { Separator } from '../../components/separator';
import { TextIcon } from '../../components/text/text-icon';
import { TextNumbering } from '../../components/text/text-numbering';
import { InfoCard } from '../../components/info';
import { useAppDispatch, useAppSelector } from '../../redux/hook';
import { useForm } from '../../hooks/use-form';
import { ApplicationStart } from '../../models/classes/ApplicationStart.class';
import { updateFormData as UpdateStepStartFormData } from '../../redux/slices/application-start-slice';
import { ProcessFlowItemInterface } from '../../models/interfaces/process-flow-item.interface';
import { ProcessFlow } from '../../components/process-flow';
// import useSaveDraft from '../../hooks/use-save-draft'
import { updateStageAStepDeclaration } from '../../redux/slices/completed-steps-slice';
import { updateFormData as UpdateStep2FormData } from '../../redux/slices/application-step-2-slice';
import { ApplicationStep2 } from '../../models/classes/ApplicationStep2.class';
import { useInitialWindowScrollTo } from '../../hooks/use-initial-window-scroll-to';
import StartApplicationAccordionInfo from './segments/start-application-accordion-info';
import useIsMobile from '../../hooks/use-is-mobile';
import { HDBLinks } from '../../utils/shared/links';
import { stageATrackingId } from '../../utils/wogaa-ids';
import { customWindow } from '../../models/interfaces/wogaa.interface';

const items: ProcessFlowItemInterface[] = [{
  id: 1,
  title: "Eligibility",
  description:<div style={{textAlign: "center"}}><p style={{marginBottom:0}}>Input reason for change, change in owners and personal details</p></div>,
  descriptionMobile: ["Proposed household structure"],
  icon: <EligibilityIcon />,
},
{
  id: 2,
  title: "Financing",
  description: <p style={{marginBottom: 0, textAlign: 'center'}}>Current outstanding loan information and details of new loan type</p>,
  descriptionMobile: ["Loan information"],
  icon: <DollarIcon />,
},
{
  id: 3,
  title: "Conveyancing",
  description: <div style={{textAlign: "center"}}><p style={{marginBottom:0}}>Appoint lawyers with details such as name, UEN of firm and contact information</p></div>,
  descriptionMobile: ["Holding type", "Lawyer's information"],
  icon: <BankIcon />,
},
{
  id: 4,
  title: "Upload Documents",
  description: <p style={{marginBottom: 0, textAlign: 'center'}}>Upload supporting documents</p>,
  descriptionMobile: ["Supporting documents"],
  icon: <DocumentIcon />,
},
{
  id: 5,
  title: "Review and Endorse",
  description: <div style={{textAlign: "center"}}><p style={{marginBottom:0}}>Endorse application, provide contact details</p></div>,
  descriptionMobile: ["Endorsements"],
  icon: <EndorsementIcon />,
}]


function StartApplicationCOO() {
  const navigate = useNavigate();
  const AppDispatch = useAppDispatch();

  const initFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const { values, setFormValue }: { handleChange: any, values: ApplicationStart, formValid: boolean, setFormValue: (update: any) => void } = useForm(new ApplicationStart(initFormData), () => { }, () => { });

  const startApplicationFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep2FormData = useAppSelector(state => state.app.ApplicationStep2Slice.formData);

  const { isMobile } = useIsMobile(1028); 
 
  /**
   * isBtnValid is to enable / disabled the start application button based on the dependencies.
   */
  const isBtnValid = useMemo((): boolean => {
    return values.checked_coo && values.checked_tnc;
  }, [values]);

  /**
  * Navigate to next page, and dispath to update values to formValue slice.
  */
  const navigateNextPage = useCallback(() => {
    AppDispatch(UpdateStepStartFormData(new ApplicationStart({...startApplicationFormData,...values})));
    AppDispatch(UpdateStep2FormData(new ApplicationStep2({...applicationStep2FormData,...values?.selected_hdb_property?.financing_info})));
    AppDispatch(updateStageAStepDeclaration(true));
    navigate('/application-coo/stage-a/eligibility');
  },[AppDispatch, applicationStep2FormData, navigate, startApplicationFormData, values])

  useInitialWindowScrollTo();

  const handleCheckCoo = useCallback(()=>{
    setFormValue({ checked_coo: !values.checked_coo })
  },[setFormValue, values.checked_coo])
  
  const handleCheckTnc = useCallback(()=>{
    setFormValue({ checked_tnc: !values.checked_tnc })
  },[setFormValue, values.checked_tnc])

  useEffect(() => {
    if (customWindow.wogaaCustom) {
      customWindow.wogaaCustom.startTransactionalService(stageATrackingId);
    }
  }, []);
 
  return (
    <ApplicationCooLayout>
      <div className='polymer-start-application'>
        <P2TextDescription color={'#333333'} description={'This e-Service allows an existing flat owner to submit an application for a change in flat ownership.'} />
        <div className='polymer-start-application__info-section'>
          <AdditionalAlert className='polymer-start-application__info-section-alert' style={{ flex: 1 }} titleContent={<AlertInfoContent />} />
          <InfoCard style={{ width: isMobile ? "unset" : "286px" }} className='polymer-start-application__info-section-info' icon={<InfoSvg />} description={"Once the first applicant has completed their part of the application, all remaining applicant(s) are required, within 7 days to verify their information, give their consent, electronically endorse the application, and upload all relevant supporting documents."} />
        </div>

        <TextNumbering style={{ marginTop: 32 }} number={1} description={'This application will be submitted to HDB after all the applicants have electronically endorsed the application and uploaded the relevant supporting documents. The form will be deleted after 28 days if there is any missing electronic signature or supporting documents.'} />
        <TextNumbering style={{marginTop: 32}} number={2} description={'Only the registered owners can apply for a change in flat ownership. All the current and proposed owners must consent to the change in flat ownership before the application is completed.'} />
        <TextNumbering style={{ marginTop: 32 }} number={3} description={<p>Please have the <a href={HDBLinks.supporting_documents}>supporting documents</a> on hand before you proceed.</p>} />
        <TextNumbering style={{ marginTop: 32 }} number={4} description={<div>
          <p>Please ensure the attached file(s) is of the following specification:</p>
          <TextNumbering style={{ marginLeft: 4 }} number={'a'} description={'PDF or JPG format'} />
          <TextNumbering style={{ marginLeft: 4 }} number={'b'} description={'Maximum 5MB per file'} />
        </div>} />
        <TextNumbering style={{ marginTop: 32 }} number={5} description={<p>Please read the <a href={HDBLinks.private_statement}>Privacy Statement</a> and <a href={HDBLinks.terms_of_use}>Terms of Use</a> before proceeding further.</p>} />
        <TextNumbering style={{marginTop: 32}} description={<p style={{fontWeight: 700}}>If you have all the required information, the entire process of Stage A: Start Application can be completed within 15 minutes. The illustration below explains the process and information expected when you start an application.</p>} />
        <div style={{marginTop: "32px", marginBottom: "32px"}}>
          <p className="polymer-start-application__process-flow-title">Stage A: Start Application</p>
          <div className="polymer-start-application__process-flow">
            <div className="polymer-start-application__process-flow-viewbox">
              <ProcessFlow items={items} activeStep={0} />
            </div>
          </div>
        </div>
        <StartApplicationAccordionInfo />
        <div style={{ marginTop: 32 }}>
          <P2TextDescription color={"#575757"} title='Declaration' description={"The HDB hereby disclaims all liability whatsoever for any inaccuracy in the information or resources available, transmitted, received or provided by the applicant through this e-Service. The HDB shall not be liable for any loss, damage or expense, including without limitation, any direct, special or consequential damage or loss suffered by any party arising or in connection or referable to and not limited to (i) any access, the use or inability to access or use this e-Service, or reliance on the information on this e-Service; or (ii) any system, server or connection failure, error, omission, interruption or delay in transmission. "} />
        </div>



        <div style={{ marginTop: 17 }}>
          <P2Checkbox isActive={values.checked_coo} onClick={handleCheckCoo} description={<p className='m-0'>I have read and understood the <a className="polymer-text-links" href={HDBLinks.process_coo}>process</a> for the Change in Flat Ownership.</p>} />
        </div>
        <div style={{ marginTop: 17 }}>
          <P2Checkbox isActive={values.checked_tnc} onClick={handleCheckTnc} description={<p className='m-0'>I have read and understood the <a className="polymer-text-links" href={`${process.env.PUBLIC_URL}/documents/Terms & Conditions for Ownership(Sep 2024).pdf`}>Terms & Conditions</a>.</p>} />
        </div>
      </div>

      <div className="polymer-button-wrapper" style={{padding: "0 32px", display: "flex", justifyContent: "center"}}>
        <P2Button style={{ marginBottom: "104px"}} variant="primary-1" disabled={!isBtnValid} onClick={navigateNextPage}>Start application</P2Button>
      </div>

    </ApplicationCooLayout>
  )
}

export default StartApplicationCOO


const AlertInfoContent = () => {
  return (
    <div style={{ flex: 1 }}>
      <TextIcon icon={<ClockSvg />} title="15 minutes to complete" weight={500}/>
      <Separator style={{ margin: "11px 0" }} />
      <TextIcon icon={<CurrencySignSVG />} title="50.00 (inclusive of GST) and valuation fees (if applicable)" weight={500}/>
      <Separator style={{ margin: "11px 0" }} />
      <TextIcon icon={<FileCheckSvg />} title="Details of proposed owner(s) and occupier(s), such as NRIC number and contact details"weight={500}/>
      <Separator style={{ margin: "11px 0" }} />
      <TextIcon icon={<FileCheckSvg />} title="Other supporting documents" weight={500}/>
    </div>
  )
}